/**
 * @fileoverview `<Input>` — primitive de saisie GNPC.
 *
 * Adapté de la primitive shadcn/ui avec les ajustements GNPC :
 *   - **Hauteur par défaut ≥ 44 px** (`h-11`) — conforme à l'exigence
 *     tactile mobile-first (Guide frontend §6.2 : "cibles ≥ 44 px").
 *     Les inputs shadcn de base sont à `h-9` (36 px), trop petits pour
 *     du remplissage OTP/téléphone au doigt sur téléphone.
 *   - **Variants de taille** `sm | md | lg` exposés via `size`.
 *   - **Slots `leftIcon` / `rightIcon`** — pattern courant en formulaires
 *     backoffice (icône loupe pour Search, icône alerte pour erreur).
 *     Rendus avec padding auto pour ne pas chevaucher le texte.
 *   - **Style d'erreur porté par `aria-invalid`** — pas une classe : le
 *     style vient donc de l'attribut ARIA, cohérent avec l'accessibilité.
 *
 * Convention : ne PAS utiliser directement dans les pages métier. Passer
 * par `<FormField>` (packages/ui/src/components/form-field.tsx) qui compose
 * Label + hint (tooltip) + Input + icône d'erreur + message.
 */

import * as React from 'react';
import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils.ts';

/**
 * Variants de taille — la hauteur est le facteur discriminant.
 * `md` (`h-11`, 44 px) est le défaut, dérogeable seulement si nécessaire
 * (formulaires très denses en backoffice → `sm`).
 */
const inputVariants = cva(
  cn(
    'flex w-full min-w-0 rounded-md border border-input bg-background text-foreground',
    'transition-[color,box-shadow] outline-none',
    'selection:bg-primary selection:text-primary-foreground',
    'placeholder:text-muted-foreground',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
    'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
    'aria-invalid:border-danger aria-invalid:ring-danger/20',
  ),
  {
    variants: {
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-3.5 text-sm',
        lg: 'h-12 px-4 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

/**
 * Props publiques de `<Input>`. On omet volontairement `size` du
 * `ComponentProps<'input'>` HTML (qui vaut `number` par le standard) pour
 * le remplacer par notre variant string — évite un conflit de typage.
 */
export interface InputProps
  extends Omit<React.ComponentProps<'input'>, 'size'>,
    VariantProps<typeof inputVariants> {
  /**
   * Icône affichée à gauche du champ (ex : `<Search />` pour un champ de
   * recherche). Rendue à `size-4`, alignée verticalement. Ne pas passer
   * de texte ici — utiliser un préfixe séparé si besoin (ex : `+225`).
   */
  leftIcon?: React.ReactNode;
  /**
   * Icône affichée à droite du champ (ex : `<AlertCircle />` en erreur,
   * `<CheckCircle2 />` en succès, `<Eye />` pour toggle password).
   * Attention à la a11y : si l'icône est purement décorative, la marquer
   * `aria-hidden`. Si elle est interactive (bouton), l'entourer d'un
   * `<button>` avec `aria-label` explicite.
   */
  rightIcon?: React.ReactNode;
}

/**
 * Composant de saisie de base.
 *
 * @example
 * ```tsx
 * <Input placeholder="Aïcha Diarra" />
 * <Input size="lg" leftIcon={<Search />} placeholder="Rechercher…" />
 * <Input aria-invalid rightIcon={<AlertCircle className="text-danger" />} />
 * ```
 */
/**
 * `forwardRef` : consommé par react-hook-form (`register`), Radix
 * (`asChild`) et tout wrapper qui doit accéder à l'input natif.
 */
const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, size, type, leftIcon, rightIcon, ...props },
  ref,
) {
  // Cas simple : pas d'icône → rendu direct (moins de DOM).
  if (!leftIcon && !rightIcon) {
    return (
      <input
        ref={ref}
        type={type}
        data-slot="input"
        className={cn(inputVariants({ size }), className)}
        {...props}
      />
    );
  }

  // Avec icône(s) : wrapper relatif + padding calculé selon la présence
  // d'icônes gauche/droite. Les icônes utilisent `pointer-events-none`
  // pour ne pas capturer le clic — l'input doit rester la cible focus.
  return (
    <div className="relative w-full">
      {leftIcon && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground [&>svg]:size-4"
        >
          {leftIcon}
        </span>
      )}
      <input
        ref={ref}
        type={type}
        data-slot="input"
        className={cn(
          inputVariants({ size }),
          leftIcon && 'pl-9',
          rightIcon && 'pr-9',
          className,
        )}
        {...props}
      />
      {rightIcon && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 [&>svg]:size-4"
        >
          {rightIcon}
        </span>
      )}
    </div>
  );
});

export { Input, inputVariants };
